// Square API服务 - 管理所有Square模块的API调用
import { apiCall, uploadFile } from './authService';

class SquareService {
  constructor() {
    this.baseURL = '/v1/square';
  }

  // ==================== 帖子相关接口 ====================

  /**
   * 获取帖子列表
   * @param {Object} params - 查询参数
   * @param {string} params.timeline - 时间线类型 ("discover" | "following")
   * @param {string} params.sort - 排序方式 ("time" | "hot")
   * @param {number} params.page - 页码 (默认: 1)
   * @param {number} params.limit - 每页数量 (默认: 20, 最大: 50)
   * @returns {Promise<Object>} 帖子列表和分页信息
   */
  async getPosts(params = {}) {
    const queryParams = new URLSearchParams({
      timeline: params.timeline || 'discover',
      sort: params.sort || 'time',
      page: params.page || 1,
      limit: params.limit || 20
    });

    return await apiCall(`${this.baseURL}/posts?${queryParams}`);
  }

  /**
   * 发布帖子
   * @param {Object} postData - 帖子数据
   * @param {string} postData.content - 帖子内容 (Markdown)
   * @param {string|null} postData.image - 图片 (base64) 或 null
   * @param {Array<string>|null} postData.tags - 标签数组 (最多5个，每个最长50字符)
   * @returns {Promise<Object>} 发布的帖子信息
   */
  async createPost(postData) {
    return await apiCall(`${this.baseURL}/posts`, {
      method: 'POST',
      body: JSON.stringify(postData)
    });
  }

  /**
   * 获取单个帖子详情
   * @param {string} postId - 帖子ID
   * @returns {Promise<Object>} 帖子详情和回复列表
   */
  async getPost(postId) {
    return await apiCall(`${this.baseURL}/posts/${postId}`);
  }

  /**
   * 删除帖子
   * @param {string} postId - 帖子ID
   * @returns {Promise<Object>} 删除结果
   */
  async deletePost(postId) {
    return await apiCall(`${this.baseURL}/posts/${postId}`, {
      method: 'DELETE'
    });
  }

  // ==================== 互动相关接口 ====================

  /**
   * 点赞帖子
   * @param {string} postId - 帖子ID
   * @returns {Promise<Object>} 点赞结果
   */
  async likePost(postId) {
    return await apiCall(`${this.baseURL}/posts/${postId}/like`, {
      method: 'POST'
    });
  }

  /**
   * 取消点赞帖子
   * @param {string} postId - 帖子ID
   * @returns {Promise<Object>} 取消点赞结果
   */
  async unlikePost(postId) {
    return await apiCall(`${this.baseURL}/posts/${postId}/like`, {
      method: 'DELETE'
    });
  }

  /**
   * 转发帖子
   * @param {string} postId - 帖子ID
   * @param {string} comment - 转发评论 (可选)
   * @returns {Promise<Object>} 转发结果
   */
  async repostPost(postId, comment = '') {
    return await apiCall(`${this.baseURL}/posts/${postId}/repost`, {
      method: 'POST',
      body: JSON.stringify({ comment })
    });
  }

  /**
   * 收藏帖子
   * @param {string} postId - 帖子ID
   * @returns {Promise<Object>} 收藏结果
   */
  async bookmarkPost(postId) {
    return await apiCall(`${this.baseURL}/posts/${postId}/bookmark`, {
      method: 'POST'
    });
  }

  /**
   * 取消收藏帖子
   * @param {string} postId - 帖子ID
   * @returns {Promise<Object>} 取消收藏结果
   */
  async unbookmarkPost(postId) {
    return await apiCall(`${this.baseURL}/posts/${postId}/bookmark`, {
      method: 'DELETE'
    });
  }

  // ==================== 回复相关接口 ====================

  /**
   * 获取帖子回复列表
   * @param {string} postId - 帖子ID
   * @param {Object} params - 查询参数
   * @param {number} params.page - 页码 (默认: 1)
   * @param {number} params.limit - 每页数量 (默认: 20)
   * @returns {Promise<Object>} 回复列表和分页信息
   */
  async getReplies(postId, params = {}) {
    const queryParams = new URLSearchParams({
      page: params.page || 1,
      limit: params.limit || 20
    });

    return await apiCall(`${this.baseURL}/posts/${postId}/replies?${queryParams}`);
  }

  /**
   * 发布回复
   * @param {string} postId - 帖子ID
   * @param {string} content - 回复内容 (Markdown)
   * @returns {Promise<Object>} 发布的回复信息
   */
  async createReply(postId, content) {
    return await apiCall(`${this.baseURL}/posts/${postId}/replies`, {
      method: 'POST',
      body: JSON.stringify({ content })
    });
  }

  /**
   * 点赞回复
   * @param {string} replyId - 回复ID
   * @returns {Promise<Object>} 点赞结果
   */
  async likeReply(replyId) {
    return await apiCall(`${this.baseURL}/replies/${replyId}/like`, {
      method: 'POST'
    });
  }

  /**
   * 取消点赞回复
   * @param {string} replyId - 回复ID
   * @returns {Promise<Object>} 取消点赞结果
   */
  async unlikeReply(replyId) {
    return await apiCall(`${this.baseURL}/replies/${replyId}/like`, {
      method: 'DELETE'
    });
  }

  // ==================== 用户关系接口 ====================

  /**
   * 关注用户
   * @param {string} userId - 用户ID
   * @returns {Promise<Object>} 关注结果
   */
  async followUser(userId) {
    return await apiCall(`${this.baseURL}/users/${userId}/follow`, {
      method: 'POST'
    });
  }

  /**
   * 取消关注用户
   * @param {string} userId - 用户ID
   * @returns {Promise<Object>} 取消关注结果
   */
  async unfollowUser(userId) {
    return await apiCall(`${this.baseURL}/users/${userId}/follow`, {
      method: 'DELETE'
    });
  }

  /**
   * 获取用户关注状态
   * @param {string} userId - 用户ID
   * @returns {Promise<Object>} 关注状态信息
   */
  async getFollowStatus(userId) {
    return await apiCall(`${this.baseURL}/users/${userId}/follow-status`);
  }

  // ==================== 用户个人数据接口 ====================

  /**
   * 获取当前用户资料
   * @returns {Promise<Object>} 用户资料信息
   */
  async getProfile() {
    return await apiCall(`${this.baseURL}/profile`);
  }

  /**
   * 更新个人资料
   * @param {Object} profileData - 个人资料数据
   * @param {string} profileData.name - 显示名称
   * @param {string} profileData.bio - 个人简介
   * @param {string} profileData.avatar - 头像URL
   * @param {string} profileData.institution - 机构/公司
   * @param {string} profileData.research_fields - 研究领域
   * @param {string} profileData.major_work - 主要工作和贡献
   * @param {string} profileData.personal_website - 个人网站
   * @param {string} profileData.orcid_id - ORCID ID
   * @param {string} profileData.resume_irys_url - 简历页面URL
   * @returns {Promise<Object>} 更新结果
   */
  async updateProfile(profileData) {
    return await apiCall(`${this.baseURL}/profile`, {
      method: 'PUT',
      body: JSON.stringify(profileData)
    });
  }

  /**
   * 上传头像图片
   * @param {File} file - 头像图片文件
   * @returns {Promise<Object>} 上传结果，包含图片URL
   */
  async uploadAvatar(file) {
    const formData = new FormData();
    formData.append('file', file);

    return await uploadFile(`${this.baseURL}/upload/image`, formData);
  }

  /**
   * 获取用户收藏列表
   * @param {Object} params - 查询参数
   * @param {number} params.page - 页码 (默认: 1)
   * @param {number} params.limit - 每页数量 (默认: 20)
   * @returns {Promise<Object>} 收藏列表和分页信息
   */
  async getBookmarks(params = {}) {
    const queryParams = new URLSearchParams({
      page: params.page || 1,
      limit: params.limit || 20
    });

    return await apiCall(`${this.baseURL}/bookmarks?${queryParams}`);
  }

  /**
   * 获取用户关注列表
   * @returns {Promise<Object>} 关注的用户列表
   */
  async getFollowing() {
    return await apiCall(`${this.baseURL}/following`);
  }

  // ==================== 统计和趋势接口 ====================

  /**
   * 获取社区统计信息
   * @returns {Promise<Object>} 社区统计数据
   */
  async getStats() {
    return await apiCall(`${this.baseURL}/stats`);
  }

  // ==================== 文件上传接口 ====================

  /**
   * 上传图片
   * @param {File} file - 图片文件 (PNG格式, <2MB)
   * @returns {Promise<Object>} 上传结果
   */
  async uploadImage(file) {
    // 验证文件类型和大小
    if (!file.type.includes('png')) {
      throw new Error('Only PNG images are supported');
    }

    if (file.size > 2 * 1024 * 1024) {
      throw new Error('Image size must be less than 2MB');
    }

    const formData = new FormData();
    formData.append('file', file);

    return await uploadFile(`${this.baseURL}/upload/image`, formData);
  }

  // ==================== 标签相关接口 ====================

  /**
   * 为帖子添加标签
   * @param {string} postId - 帖子ID
   * @param {Array<string>} tags - 标签数组 (最多5个，每个最长50字符)
   * @returns {Promise<Object>} 更新后的帖子信息
   */
  async addTagsToPost(postId, tags) {
    return await apiCall(`${this.baseURL}/posts/${postId}/tags`, {
      method: 'POST',
      body: JSON.stringify({ tags })
    });
  }

  /**
   * 获取热门标签
   * @param {number} limit - 返回数量 (默认: 20, 最大: 100)
   * @returns {Promise<Object>} 热门标签列表
   */
  async getPopularTags(limit = 20) {
    const queryParams = new URLSearchParams({
      limit: Math.min(limit, 100)
    });

    return await apiCall(`${this.baseURL}/tags?${queryParams}`);
  }

  // ==================== 搜索接口 ====================

  /**
   * 搜索帖子
   * @param {Object} params - 搜索参数
   * @param {string} params.q - 搜索关键词 (必填)
   * @param {string} params.sort - 排序方式 ("time" | "replies") (默认: "time")
   * @param {number} params.page - 页码 (默认: 1)
   * @param {number} params.limit - 每页数量 (默认: 20, 最大: 50)
   * @returns {Promise<Object>} 搜索结果
   */
  async searchPosts(params) {
    if (!params.q || !params.q.trim()) {
      throw new Error('Search query is required');
    }

    const queryParams = new URLSearchParams({
      q: params.q.trim(),
      sort: params.sort || 'time',
      page: params.page || 1,
      limit: Math.min(params.limit || 20, 50)
    });

    return await apiCall(`${this.baseURL}/search?${queryParams}`);
  }

  // ==================== 工具方法 ====================

  /**
   * 验证标签格式
   * @param {Array<string>} tags - 标签数组
   * @returns {Object} 验证结果 {valid: boolean, error?: string}
   */
  validateTags(tags) {
    if (!Array.isArray(tags)) {
      return { valid: false, error: 'Tags must be an array' };
    }

    if (tags.length > 5) {
      return { valid: false, error: 'Maximum 5 tags allowed' };
    }

    for (const tag of tags) {
      if (typeof tag !== 'string') {
        return { valid: false, error: 'All tags must be strings' };
      }

      if (tag.length > 50) {
        return { valid: false, error: 'Tag length cannot exceed 50 characters' };
      }

      if (tag.trim().length === 0) {
        return { valid: false, error: 'Tags cannot be empty' };
      }
    }

    return { valid: true };
  }

  /**
   * 格式化标签（转小写，去空格）
   * @param {Array<string>} tags - 原始标签数组
   * @returns {Array<string>} 格式化后的标签数组
   */
  formatTags(tags) {
    if (!Array.isArray(tags)) return [];

    return tags
      .map(tag => tag.trim().toLowerCase())
      .filter(tag => tag.length > 0)
      .slice(0, 5); // 确保最多5个标签
  }

  /**
   * 将文件转换为base64
   * @param {File} file - 文件对象
   * @returns {Promise<string>} base64字符串
   */
  async fileToBase64(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }

  /**
   * 格式化时间显示 - 同时显示绝对时间和相对时间
   * @param {string} timestamp - ISO时间戳
   * @returns {string} 格式化的时间字符串
   */
  formatTime(timestamp) {
    // 确保时间戳正确解析为UTC时间
    let time;
    if (timestamp.endsWith('Z') || timestamp.includes('+') || timestamp.includes('-')) {
      // 已经是UTC或带时区信息的时间戳
      time = new Date(timestamp);
    } else {
      // 假设是UTC时间但没有Z后缀，添加Z
      time = new Date(timestamp + (timestamp.includes('T') ? 'Z' : 'T00:00:00Z'));
    }

    const now = new Date();
    const diff = now - time;

    // 计算相对时间
    const seconds = Math.floor(diff / 1000);
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    let relativeTime;
    if (seconds < 60) {
      relativeTime = 'just now';
    } else if (minutes < 60) {
      relativeTime = `${minutes}m ago`;
    } else if (hours < 24) {
      relativeTime = `${hours}h ago`;
    } else if (days < 7) {
      relativeTime = `${days}d ago`;
    } else {
      relativeTime = `${Math.floor(days / 7)}w ago`;
    }

    // 格式化绝对时间（使用本地时区）
    const absoluteTime = time.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone // 使用用户本地时区
    });

    // 返回组合格式：相对时间 · 绝对时间
    return `${relativeTime} · ${absoluteTime}`;
  }
}

// 创建并导出单例实例
const squareService = new SquareService();
export default squareService;
